import { useState, useCallback, useEffect } from "react";
import {
  submitAfterImageQuestionnaire,
  submitAfterPhaseQuestionnaire,
  submitEndingQuestionnaire,
  submitStartingQuestionnaire,
} from "../api/questionnaire";

export const useQuestionnaireManager = ({
  logUserAction,
  phaseIndex,
  imageIndex,
  assignedMode,
  currentImage,
  imagesForMode,
  phaseOrder,
  setImageIndex,
  setPhaseIndex,
  participantCode,
  restoredProgress,
  onProgress,
}) => {
  // Questionnaire states
  // Restored on a refresh so the participant is not asked to re-submit the
  // starting questionnaire (which would create a duplicate DB row).
  const [startingQuestionnaireSubmitted, setStartingQuestionnaireSubmitted] =
    useState(!!restoredProgress?.startingQuestionnaireSubmitted);
  const [startingQuestionnaireData, setStartingQuestionnaireData] = useState(
    restoredProgress?.startingQuestionnaireData
  );

  // Show/hide questionnaires
  const [showAfterImageQuestionnaire, setShowAfterImageQuestionnaire] =
    useState(false);
  const [showAfterPhaseQuestionnaire, setShowAfterPhaseQuestionnaire] =
    useState(false);
  const [showEndingQuestionnaire, setShowEndingQuestionnaire] = useState(false);

  // Track questionnaire start times
  const [questionnaireStartTimes, setQuestionnaireStartTimes] = useState({});

  // Helper function to determine explanation availability based on mode
  const getExplanationAvailability = useCallback((modeKey) => {
    const isTextualExplanationAvailable = [
      "SIMPLE VISUAL",
      "Interactive Textual XAI method",
      "Interactive Visual XAI method",
      "Interactive Complementary XAI method",
    ].includes(modeKey);

    const isVisualExplainationAvailable = [
      "Interactive Visual XAI method",
      "Interactive Complementary XAI method",
    ].includes(modeKey);

    return {
      isTextualExplanationAvailable,
      isVisualExplainationAvailable,
    };
  }, []);

  // Handlers
  const handleStartingQuestionnaireSubmission = useCallback(
    async (fd) => {
      // Log the starting questionnaire submission
      const logData = {
        participantCode: fd.participantCode,
        logs: {
          participantCode: fd.participantCode,
          assignedMode: "N/A",
          imageurl: "N/A",
          activity: "Starting Questionnaire Submission",
          startTime: new Date().toISOString(),
          endTime: "N/A",
          durationMs: "N/A",
          durationSeconds: "N/A",
          element: "N/A",
        },
      };
      await logUserAction(
        "Starting Questionnaire Submission",
        {
          participantCode: fd.participantCode,
          assignedMode: "N/A",
          currentImage: null,
        },
        logData.logs
      );

      // Submit starting questionnaire to backend
      await submitStartingQuestionnaire({
        participantCode: fd.participantCode,
        data: fd,
      });

      setStartingQuestionnaireData(fd);
      setStartingQuestionnaireSubmitted(true);
      // Record the milestone so a refresh resumes past this form instead of
      // re-submitting it (which would insert a duplicate row).
      onProgress?.({
        startingQuestionnaireSubmitted: true,
        startingQuestionnaireData: fd,
      });
    },
    [logUserAction, onProgress]
  );

  const handleAfterImageQuestionnaireSubmission = useCallback(
    async (fd) => {
      const currentTime = new Date().toISOString();
      const questionnaireKey = `afterImage_${phaseIndex}_${imageIndex}`;

      // Calculate questionnaire completion time
      let questionnaireDuration = null;
      let questionnaireDurationSeconds = null;
      const startTime = questionnaireStartTimes[questionnaireKey];
      if (startTime) {
        questionnaireDuration = new Date(currentTime) - new Date(startTime);
        questionnaireDurationSeconds =
          Math.round((questionnaireDuration / 1000) * 100) / 100;
      }

      // Log the after image questionnaire submission with timing
      await logUserAction(
        "After Image Questionnaire Submission",
        {
          participantCode: startingQuestionnaireData?.participantCode,
          assignedMode,
          currentImage,
        },
        {
          formData: fd,
          imageIndex: imageIndex,
          startTime: startTime,
          endTime: currentTime,
          durationMs: questionnaireDuration,
          durationSeconds: questionnaireDurationSeconds,
        }
      );

      await submitAfterImageQuestionnaire({
        participantCode: startingQuestionnaireData?.participantCode,
        assignedMode: assignedMode,
        imageurl: currentImage?.imageUrl,
        data: fd,
      });
      setShowAfterImageQuestionnaire(false);
      // Next image or after-phase questionnaire
      if (imageIndex < imagesForMode?.length - 1) {
        setImageIndex(imageIndex + 1);
      } else {
        setShowAfterPhaseQuestionnaire(true);
      }
    },
    [
      phaseIndex,
      imageIndex,
      questionnaireStartTimes,
      logUserAction,
      startingQuestionnaireData,
      assignedMode,
      currentImage,
      imagesForMode,
      setImageIndex,
    ]
  );


  const handleAfterPhaseQuestionnaireSubmission = useCallback(
    async (fd) => {
      const currentTime = new Date().toISOString();
      const questionnaireKey = `afterMode_${phaseIndex}`;

      // Calculate questionnaire completion time
      let questionnaireDuration = null;
      let questionnaireDurationSeconds = null;
      const startTime = questionnaireStartTimes[questionnaireKey];
      if (startTime) {
        questionnaireDuration = new Date(currentTime) - new Date(startTime);
        questionnaireDurationSeconds =
          Math.round((questionnaireDuration / 1000) * 100) / 100;
      }

      // Log the after mode questionnaire submission with timing
      await logUserAction(
        "After Mode Questionnaire Submission",
        {
          participantCode: startingQuestionnaireData?.participantCode,
          assignedMode,
          currentImage,
        },
        {
          formData: fd,
          phaseIndex: phaseIndex,
          assignedMode: assignedMode,
          startTime: startTime,
          endTime: currentTime,
          durationMs: questionnaireDuration,
          durationSeconds: questionnaireDurationSeconds,
        }
      );

      await submitAfterPhaseQuestionnaire({
        participantCode: startingQuestionnaireData?.participantCode,
        assignedMode: assignedMode,
        data: fd,
      });
      setShowAfterPhaseQuestionnaire(false);
      // Next phase or ending questionnaire
      if (phaseIndex < phaseOrder?.length - 1) {
        setPhaseIndex(phaseIndex + 1);
        setImageIndex(0);
      } else {
        setShowEndingQuestionnaire(true);
      }
    },
    [
      phaseIndex,
      questionnaireStartTimes,
      logUserAction,
      startingQuestionnaireData,
      assignedMode,
      currentImage,
      phaseOrder,
      setPhaseIndex,
      setImageIndex,
    ]
  );

  const handleEndingQuestionnaireSubmission = useCallback(
    async (fd) => {
      const currentTime = new Date().toISOString();
      const questionnaireKey = "ending";

      // Calculate questionnaire completion time
      let questionnaireDuration = null;
      let questionnaireDurationSeconds = null;
      const startTime = questionnaireStartTimes[questionnaireKey];
      if (startTime) {
        questionnaireDuration = new Date(currentTime) - new Date(startTime);
        questionnaireDurationSeconds =
          Math.round((questionnaireDuration / 1000) * 100) / 100;
      }

      // Log the ending questionnaire submission with timing
      await logUserAction(
        "Ending Questionnaire Submission",
        {
          participantCode: startingQuestionnaireData?.participantCode,
          assignedMode,
          currentImage,
        },
        {
          formData: fd,
          studyCompleted: true,
          startTime: startTime,
          endTime: currentTime,
          durationMs: questionnaireDuration,
          durationSeconds: questionnaireDurationSeconds,
        }
      );

      await submitEndingQuestionnaire({
        participantCode: startingQuestionnaireData?.participantCode,
        assignedMode: assignedMode,
        data: fd,
      });
    },
    [questionnaireStartTimes, logUserAction, startingQuestionnaireData, assignedMode, currentImage]
  );

  // Track questionnaire start times
  useEffect(() => {
    const currentTime = new Date().toISOString();

    if (showAfterImageQuestionnaire) {
      const questionnaireKey = `afterImage_${phaseIndex}_${imageIndex}`;
      setQuestionnaireStartTimes((prev) => ({
        ...prev,
        [questionnaireKey]: currentTime,
      }));

      logUserAction(
        "After Image Questionnaire Started",
        {
          participantCode: startingQuestionnaireData?.participantCode,
          assignedMode,
          currentImage,
        },
        {
          questionnaireKey: questionnaireKey,
          questionnaireStartTime: currentTime,
          imageIndex: imageIndex,
        }
      );
    }

    if (showAfterPhaseQuestionnaire) {
      const questionnaireKey = `afterMode_${phaseIndex}`;
      setQuestionnaireStartTimes((prev) => ({
        ...prev,
        [questionnaireKey]: currentTime,
      }));

      logUserAction(
        "After Mode Questionnaire Started",
        {
          participantCode: startingQuestionnaireData?.participantCode,
          assignedMode,
          currentImage,
        },
        {
          questionnaireKey: questionnaireKey,
          questionnaireStartTime: currentTime,
          phaseIndex: phaseIndex,
          assignedMode: assignedMode,
        }
      );
    }

    if (showEndingQuestionnaire) {
      const questionnaireKey = "ending";
      setQuestionnaireStartTimes((prev) => ({
        ...prev,
        [questionnaireKey]: currentTime,
      }));

      logUserAction(
        "Ending Questionnaire Started",
        {
          participantCode: startingQuestionnaireData?.participantCode,
          assignedMode,
          currentImage,
        },
        {
          questionnaireKey: questionnaireKey,
          questionnaireStartTime: currentTime,
        }
      );
    }
  }, [
    showAfterImageQuestionnaire,
    showAfterPhaseQuestionnaire,
    showEndingQuestionnaire,
    phaseIndex,
    imageIndex,
    assignedMode,
    logUserAction,
    currentImage,
    startingQuestionnaireData?.participantCode,
  ]);

  return {
    // State
    startingQuestionnaireSubmitted,
    startingQuestionnaireData,
    showAfterImageQuestionnaire,
    showAfterPhaseQuestionnaire,
    showEndingQuestionnaire,
    questionnaireStartTimes,

    // Functions
    getExplanationAvailability,
    handleStartingQuestionnaireSubmission,
    handleAfterImageQuestionnaireSubmission,
    handleAfterPhaseQuestionnaireSubmission,
    handleEndingQuestionnaireSubmission,
    setShowAfterImageQuestionnaire,
    setShowAfterPhaseQuestionnaire,
    setShowEndingQuestionnaire,
    setQuestionnaireStartTimes,
  };
};
